import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteField,
  where,
  type Firestore,
} from 'firebase/firestore';
import { LIA_MODEL_AUTO, liaModelChoice } from './lia-models.js';

/** Reglage non secret de l'acces IA de l'organisation. Ferme par defaut. */
export interface IaSettings {
  enabled: boolean;
  launcher: 'mascot' | 'bubble';
  /**
   * Modele de Lia choisi par le proprietaire (`LIA_MODEL_CHOICES`), `auto` pour Jev.
   * `null` : le defaut du projet. L'ancien interrupteur `jevBeta` se lit comme `auto`.
   */
  model: string | null;
}

export interface IaConnection {
  id: string;
  uid: string;
  email: string | null;
  clientName: string;
  connectedAt: Date | null;
  lastSeenAt: Date | null;
  revokedAt: Date | null;
}

export interface IaSettingsRepository {
  observer(onValue: (settings: IaSettings) => void, onError?: (error: unknown) => void): () => void;
  setEnabled(enabled: boolean): Promise<void>;
  setLauncher(launcher: IaSettings['launcher']): Promise<void>;
  setModel(model: string | null): Promise<void>;
  connections(includeAll?: boolean): Promise<IaConnection[]>;
  revokeConnection(id: string): Promise<void>;
}

function date(value: unknown): Date | null {
  return value != null && typeof value === 'object' && 'toDate' in value &&
    typeof value.toDate === 'function' ? value.toDate() as Date : null;
}

export function createIaSettingsRepository(db: Firestore, getActorId: () => string, orgId: string): IaSettingsRepository {
  const settingsRef = doc(db, 'orgs', orgId, 'aiSettings', 'default');
  const connectionsRef = collection(db, 'orgs', orgId, 'aiConnections');
  return {
    observer(onValue, onError) {
      return onSnapshot(settingsRef, (snapshot) => {
        onValue({ enabled: snapshot.exists() && snapshot.data()['enabled'] === true,
          launcher: snapshot.data()?.['launcher'] === 'bubble' ? 'bubble' : 'mascot',
          model: liaModelChoice(snapshot.data()?.['model']) ?? (snapshot.data()?.['jevBeta'] === true ? LIA_MODEL_AUTO : null) });
      }, onError);
    },
    async setEnabled(enabled) {
      await setDoc(settingsRef, {
        orgId,
        enabled,
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      }, { merge: true });
    },
    async setLauncher(launcher) {
      await runTransaction(db, async (transaction) => {
        const current = await transaction.get(settingsRef);
        transaction.set(settingsRef, {
          orgId,
          enabled: current.exists() && current.data()['enabled'] === true,
          launcher,
          updatedAt: serverTimestamp(),
          updatedBy: getActorId(),
        }, { merge: true });
      });
    },
    async setModel(model) {
      await runTransaction(db, async (transaction) => {
        const current = await transaction.get(settingsRef);
        transaction.set(settingsRef, {
          orgId,
          enabled: current.exists() && current.data()['enabled'] === true,
          // Le menu remplace l'ancien interrupteur Jev : il ne doit plus rien decider.
          model: model === null ? deleteField() : model,
          jevBeta: deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: getActorId(),
        }, { merge: true });
      });
    },
    async connections(includeAll = false) {
      const snapshot = await getDocs(includeAll ? connectionsRef : query(connectionsRef, where('uid', '==', getActorId())));
      return snapshot.docs.map((entry) => {
        const data = entry.data();
        return {
          id: entry.id,
          uid: typeof data['uid'] === 'string' ? data['uid'] : '',
          email: typeof data['email'] === 'string' ? data['email'] : null,
          clientName: typeof data['clientName'] === 'string' ? data['clientName'] : 'Client MCP',
          connectedAt: date(data['connectedAt']),
          lastSeenAt: date(data['lastSeenAt']),
          revokedAt: date(data['revokedAt']),
        };
      });
    },
    async revokeConnection(id) {
      await updateDoc(doc(connectionsRef, id), { revokedAt: serverTimestamp(), revokedBy: getActorId() });
    },
  };
}
